/* 
eslint-disable no-mixed-operators
*/
import React from 'react';
import dlv from 'dlv';
import cx from 'classnames';
import { getLocalizedText, localizeObject } from '../../../../i18n';
import { inResults} from '../../../../service';
import Form, { GeneralErrorText, useSubmit, useFormData, collectErrors, rule, useValidate, fieldsForService } from '../../../core/form';
import { match, nomatch, fieldsMatch } from '../../../../util/validation';
import { callWith, useLater, matchAll, isString, addSearchParams, callAll, applyWindowProps } from '../../../../util';
import { fillTemplate, fillTemplatedObject } from '../../../../util/template';
import generateComponent from './components';

function chooseValidator({field, message, errorAs, required, not, ...props}, t) {
	if(props.match) return rule(field, fieldsMatch(props.match, field, getLocalizedText(t,message)), null, required, errorAs);
	if(props.pattern) {
		if(not) {
			return rule(field, nomatch(props.pattern, getLocalizedText(t,message)), required, errorAs);
		} else {
			return rule(field, match(props.pattern, getLocalizedText(t,message)), required, errorAs);
		}
		
	}
	return () => true;
}

export const DynamicForm = React.memo(
	({ screenHasInit, descriptor, t, componentConfig,
	 previousScreenResults, serviceDescriptors, 
	 suspense, serviceResult, onRequestScreen, onCallService,
	 clearErrors, resultNotify, errors,
	 ...props
	}) => {

		const [formRef, setFormRef] = React.useState();

		/*
		We are going to delay the ability to submit by a short period of time.
		Was seeing consecutive forms triggering the onSubmit event of the next 
		when using the enter key. This is a workaround:
		*/
		const [allowSubmit] = useLater(true, 100);
		const controls = dlv(descriptor, 'controls');
		const submitOnEnter = dlv(descriptor, 'submitOnEnter');
		const [ formData, bindAPI ] = useFormData();

		/*
			Lookup Order:
				* Most recent service result.
				* Current form data.
				* Previous results.
		*/
		const findDynamicValue = (key) => serviceResult && dlv(serviceResult, key) || formData && dlv(formData, key)  || inResults(previousScreenResults, key) || '';
		
		/*
			The following code has 2 calls to fillTemplate. This happens because we need to ensure 
			that template vars sent into localiztion calls are resolved forst, but also localization 
			text might return template vars that also need to be resolved:
		*/
		const dynamicText = (value, extraProps) => fillTemplate(getLocalizedText(t, fillTemplate(value, findDynamicValue, extraProps)),findDynamicValue, extraProps);
		const dynamicValue = (value, extraProps) => fillTemplate(value, findDynamicValue, extraProps);
		const dynamicProps = (obj, extraProps) => fillTemplatedObject(localizeObject(t, obj), findDynamicValue, extraProps);

		const validationRules = dlv(descriptor, 'validation', []).map(args => chooseValidator(dynamicProps(args), t));
		const [ validationErrors, validate ] = useValidate(validationRules);

		const action = dlv(descriptor, 'action');
		const method = dlv(descriptor, 'method');

		const directSubmit = (data) => {
			if (action && formRef) {
				if(data) {
					const defaultAction = formRef.current.action;
					const origUrl = new URL(defaultAction);
					addSearchParams(origUrl.searchParams, new URLSearchParams(data));
					formRef.current.action = origUrl.toString();
				}
				formRef.current.submit();
			};
		};

		const submit = useSubmit(
			formData,
			(data, serviceId, formQuery) => (
				validate(data, fieldsForService(serviceId, serviceDescriptors)) && (action ? directSubmit(formQuery) : onCallService(data, serviceId || descriptor.service))
			), 
			() => clearErrors && clearErrors() //this clears errors from form before each validate and submit call
		);

		const shouldLiveValidate = dlv(descriptor, 'liveValidationEnabled');
		const allRequiredFields = validationRules.filter(v => v.required !== false).reduce((acc, v) => {
			!acc.includes(v.field) && acc.push(v.field);
			return acc;
		}, []);
		const [ visitedRequiredFields, setVisitedRequiredFields ] = React.useState([]);

		const submitDisabled = shouldLiveValidate && (
			(visitedRequiredFields.length < allRequiredFields.length) ||
			Object.keys(validationErrors).length > 0
		);

		const handleLiveValidation = field => {
			if (allRequiredFields.includes(field)) {
				if (!visitedRequiredFields.includes(field)) {
					visitedRequiredFields.push(field);
					setVisitedRequiredFields(visitedRequiredFields);
				}

				const validateFields = visitedRequiredFields.reduce(
					(acc, fieldKey) => {
						acc[fieldKey] = formData[fieldKey];
						return acc;
					}, {}
				);

				validate(validateFields);
			}
		}

		const bindLiveValidate = field => shouldLiveValidate && { onBlur: callWith(handleLiveValidation, field) };

		errors = collectErrors(errors, validationErrors);

		const conditionCheck = {
			...(serviceResult || {}),
			...(Object.keys(errors).length ? {error:errors} : {})
		};

		const checkIndividualCondition = (condition, extraLookupData) => {
			if(!condition) return false;
			const [path, value] = condition.split(':');
			const lookup = dlv(formData, path) || dlv(conditionCheck, path) || inResults(previousScreenResults, path) || (extraLookupData && dlv(extraLookupData, path));
			const fValue = isString(value) ? (dlv(formData, value) || dlv(conditionCheck, value) || inResults(previousScreenResults, value) || (extraLookupData && dlv(extraLookupData, value)) || value) : value;
			if(fValue) {
				const valueAsStringString = fValue !== undefined ? fValue.toString() : 'null';
				return lookup === valueAsStringString;
			}
			return !!lookup;
		}
		
		const onCheckCondition = (condition, extraLookupData) => {
			if(!condition) return false;
			const match = matchAll(condition, /(.+?)(?=\|\||\&\&|$)/); //eslint-disable-line no-useless-escape
			const conditions =  match.map(v => {
				const str = v[0];
				const isOr = str.indexOf("||") > -1;
				const isAnd = str.indexOf("&&") > -1;
				const strippedStr = (isOr ? str.split("||")[1] : isAnd ? str.split("&&")[1] : str).trim();
				return {condition:strippedStr, isOr, isAnd};
			});
			let lastEval = true;
			for(let i = 0; i < conditions.length; i++) {
				const c = conditions[i];
				const evalC = checkIndividualCondition(c.condition, extraLookupData);
				if(c.isOr) {
					lastEval = evalC || lastEval;
				} else if (c.isAnd) {
					lastEval = lastEval && evalC;
				} else {
					lastEval = evalC;
				}
				if(i > 0 && !lastEval) return lastEval;
			}
			return lastEval;

		}

		const onButtonClick = (controlProps) => {
			const winProps = controlProps.windowProps ? () => applyWindowProps(controlProps.windowProps): null;
			if(controlProps.screen)return callAll(winProps, callWith(onRequestScreen, controlProps.screen));
			return callAll(
				winProps,
				controlProps.submitFormData 
					? handleSubmit(controlProps)
					: callWith(onCallService, controlProps.serviceArgs, controlProps.service)
			);
		}
		let defaultSubmitService = null;

		const handleSubmit = (controlProps) => {
			if(controlProps && controlProps.service && controlProps.formDefault)defaultSubmitService = controlProps.service;
			return () => {
				if (!allowSubmit) return;
				return submit(controlProps ? controlProps.service : defaultSubmitService, controlProps && controlProps.formQuery);
			};
		}

		const formClassName = componentConfig && componentConfig.classNames && componentConfig.classNames.form;
		return (
			<Form onSubmit={submitOnEnter !== false && handleSubmit()} onForm={setFormRef} className={cx(formClassName)} action={dynamicValue(action)} method={dynamicValue(method)} {...props}>
				<GeneralErrorText msg={dlv(errors, 'general')}/>
				{controls && controls.map((compDef, i) => {
					const key = `control-${i}`;
					return generateComponent(
						{...compDef, key},
						{
							dynamicText, dynamicProps, dynamicValue, findDynamicValue,
							submitDisabled,
							suspense, 
							onButtonClick, 
							onCheckCondition, 
							bindLiveValidate,
							errors,
							screenHasInit, 
							...bindAPI,
							onRequestScreen, resultNotify
						},
						componentConfig
					);
				})}
			</Form>
		);
	}
);

export default DynamicForm;